/**
 * Carrier MCP stdio — ui_agent_schedule_* stubs, and a live ui_agent_usage.
 *
 * The five schedule tools are stubs because a CLI cannot host a schedule. See
 * `ui-agent-stub.ts` for the full reasoning: Browser Use Cloud has no native
 * scheduler, so the Worker rebuilt one on CF KV plus a Cron Trigger with leader
 * election, and a process that exits when its client disconnects has nothing to
 * offer in its place. Accepting a schedule here and silently never firing it
 * would be worse than refusing.
 *
 * `ui_agent_usage` is different, and is wired live. On the Worker it reports
 * spend from KV counters written across all sessions. Here it reports what THIS
 * CLI session actually spent, accumulated from the vendor-billed figure in the
 * audit row `@carrier/ai` emits. That is a narrower number than the Worker's,
 * and it says so in the payload rather than presenting a per-process figure as
 * if it were an account total — a usage tool that quietly under-reports is how
 * a spend surprise happens.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolContext } from "./tools.js";
import type { ToolScope } from "./types.js";
import { uiAgentStubError, type ToolResult } from "./ui-agent-stub.js";
import {
  MAX_RUNS_ENV,
  type StdioUiAgentRuntime,
} from "./ui-agent-runtime.js";

/** Doc + registry merge — keep aligned with the Worker's registry. */
export const UI_AGENT_SCHEDULE_TOOL_SCOPES: Record<string, ToolScope> = {
  ui_agent_schedule_create: "admin",
  ui_agent_schedule_list: "read",
  ui_agent_schedule_delete: "admin",
  ui_agent_schedule_pause: "admin",
  ui_agent_schedule_resume: "admin",
  ui_agent_usage: "read",
};

/** Appended to each scheduling tool's description, so tools/list is honest. */
const STDIO_NOTE =
  " Requires the remote Carrier MCP Worker deployment — a local CLI has no recurring runtime, so schedules cannot fire here.";

function scopeError(
  toolName: string,
  required: string,
  actual: string[],
): ToolResult {
  return {
    isError: true,
    content: [
      {
        type: "text" as const,
        text: `Scope denied: tool '${toolName}' requires '${required}' scope. Your token has: [${actual.join(", ")}].`,
      },
    ],
  };
}

/**
 * Audit the refusal and answer with it.
 *
 * The `ctx.props.scope.includes(...)` test is written out at each call site
 * rather than hidden in here, deliberately. `ui-agent-storefront-scope.test.ts`
 * checks that every registered tool's own registration window contains a scope
 * check, because scope fixes kept landing on one half of the fork and a helper
 * call reads as no check at all to that scan. Inlining the condition keeps the
 * cross-surface guard meaningful.
 *
 * Scope is checked before the stub refusal even though the stub refuses
 * everyone. That is not access control — it is the two surfaces answering the
 * same question the same way. An under-scoped caller is told their scope is
 * wrong, exactly as the hosted Worker tells them, instead of being sent to the
 * Worker where the same call would then be refused for a different reason.
 */
function scopeDenied(
  ctx: ToolContext,
  toolName: string,
  required: ToolScope,
): ToolResult {
  ctx.audit({
    tool_name: toolName,
    ocs_method: `[ui-agent:${toolName}]`,
    status: "scope_denied",
    dry_run: false,
    duration_ms: 0,
  });
  return scopeError(toolName, required, ctx.props.scope);
}

export function registerScheduleAndUsageTools(
  server: McpServer,
  ctx: ToolContext,
  runtime: StdioUiAgentRuntime,
): void {
  server.registerTool(
    "ui_agent_schedule_create",
    {
      title: "Create UI Agent Schedule",
      description:
        "Creates a recurring browser-agent run on a cron schedule, to automate periodic OCS audits, " +
        "fleet health checks, or any recurring browser-automation task. Minimum interval: 5 minutes. " +
        "Requires admin scope." +
        STDIO_NOTE,
      inputSchema: {
        name: z.string().describe("Human-readable name for this schedule"),
        cron: z
          .string()
          .describe(
            "Standard 5-field cron expression (minute hour day month weekday). " +
              "Minimum interval: 5 minutes. Example: '0 */6 * * *' = every 6 hours.",
          ),
        prompt_template: z
          .string()
          .describe(
            "Agent prompt/task template the browser agent will execute on each run",
          ),
        profile: z
          .string()
          .optional()
          .describe(
            "Browser-agent profile to use. Defaults to the deployment's configured profile.",
          ),
      },
    },
    async (): Promise<ToolResult> =>
      !ctx.props.scope.includes("admin")
        ? scopeDenied(ctx, "ui_agent_schedule_create", "admin")
        : uiAgentStubError("ui_agent_schedule_create", "recurring_runtime"),
  );

  server.registerTool(
    "ui_agent_schedule_list",
    {
      title: "List UI Agent Schedules",
      description:
        "Lists all recurring browser-agent schedules configured for this deployment. " +
        "Returns schedule IDs, names, cron expressions, status (active/paused), " +
        "and next/last run timestamps." +
        STDIO_NOTE,
      inputSchema: {},
    },
    async (): Promise<ToolResult> =>
      !ctx.props.scope.includes("read")
        ? scopeDenied(ctx, "ui_agent_schedule_list", "read")
        : uiAgentStubError("ui_agent_schedule_list", "recurring_runtime"),
  );

  server.registerTool(
    "ui_agent_schedule_delete",
    {
      title: "Delete UI Agent Schedule",
      description:
        "Permanently deletes a recurring browser-agent schedule. This cannot be undone. " +
        "Use ui_agent_schedule_pause to temporarily suspend instead. Requires admin scope." +
        STDIO_NOTE,
      inputSchema: {
        schedule_id: z.string().describe("ID of the schedule to delete"),
      },
    },
    async (): Promise<ToolResult> =>
      !ctx.props.scope.includes("admin")
        ? scopeDenied(ctx, "ui_agent_schedule_delete", "admin")
        : uiAgentStubError("ui_agent_schedule_delete", "recurring_runtime"),
  );

  server.registerTool(
    "ui_agent_schedule_pause",
    {
      title: "Pause UI Agent Schedule",
      description:
        "Pauses an active recurring browser-agent schedule. The schedule is preserved and can be " +
        "resumed later with ui_agent_schedule_resume. Requires admin scope." +
        STDIO_NOTE,
      inputSchema: {
        schedule_id: z.string().describe("ID of the schedule to pause"),
      },
    },
    async (): Promise<ToolResult> =>
      !ctx.props.scope.includes("admin")
        ? scopeDenied(ctx, "ui_agent_schedule_pause", "admin")
        : uiAgentStubError("ui_agent_schedule_pause", "recurring_runtime"),
  );

  server.registerTool(
    "ui_agent_schedule_resume",
    {
      title: "Resume UI Agent Schedule",
      description:
        "Resumes a paused recurring browser-agent schedule. Requires admin scope." +
        STDIO_NOTE,
      inputSchema: {
        schedule_id: z.string().describe("ID of the schedule to resume"),
      },
    },
    async (): Promise<ToolResult> =>
      !ctx.props.scope.includes("admin")
        ? scopeDenied(ctx, "ui_agent_schedule_resume", "admin")
        : uiAgentStubError("ui_agent_schedule_resume", "recurring_runtime"),
  );

  // =========================================================================
  // ui_agent_usage — live, but session-scoped and explicit about it
  // =========================================================================
  server.registerTool(
    "ui_agent_usage",
    {
      title: "UI Agent Usage & Spend",
      description:
        "Reports browser-agent spend for the current CLI session: runs dispatched, real vendor-billed " +
        "cost in USD, and how many runs remain under the session limit. " +
        "This is a per-process figure, not an account total — it resets when the CLI restarts, and it " +
        "cannot see runs billed to the same API key by other processes or by the hosted Worker. " +
        "Check your Browser Use account for the authoritative balance. Requires read scope.",
      inputSchema: {},
    },
    async (): Promise<ToolResult> => {
      if (!ctx.props.scope.includes("read")) {
        ctx.audit({
          tool_name: "ui_agent_usage",
          ocs_method: "[ui-agent:usage]",
          status: "scope_denied",
          dry_run: false,
          duration_ms: 0,
        });
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Scope denied: tool 'ui_agent_usage' requires 'read' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
            },
          ],
        };
      }

      return {
        content: [
          {
            type: "text" as const,
            text: JSON.stringify(
              {
                scope: "cli_session",
                configured: runtime.configured,
                runs_billed_this_session: runtime.spend.runs,
                cost_usd_this_session: runtime.spend.costUsd,
                runs_dispatched_this_session: runtime.budget.spent,
                runs_remaining_this_session: runtime.budget.remaining,
                run_limit_env: MAX_RUNS_ENV,
                note:
                  "Session-scoped. Resets when this CLI process exits and does not include runs " +
                  "billed to the same key elsewhere. The authoritative balance is in your Browser Use account.",
              },
              null,
              2,
            ),
          },
        ],
      };
    },
  );
}
